/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { Row } from "@/components";
// Unchanged shared form piece — still lives with the legacy sheet until that
// folder is deleted.
import { ChangeStatusDialogRequisitionSelect } from "./ChangeStatusDialogRequisitionSelect";
import { FormCurrencyInput } from "@/components/form/FormCurrencyInput";
import { FormDateInput } from "@/components/form/FormDateInput";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { FormNumberInput } from "@/components/form/FormNumberInput";
import { FormNumberSelect } from "@/components/form/FormNumberSelect";
import { FormTextInput } from "@/components/form/FormTextInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useReportUnexpectedNull } from "@/hooks/useReportUnexpectedNull";
import { useCreatePlacement } from "@/hooks/mutations/useCreatePlacement";
import { useUpdatePlacement } from "@/hooks/mutations/useUpdatePlacement";
import { useGetRequisitions } from "@/hooks/queries/useGetRequisitions";
import { useAddPlacement } from "@/hooks/sets/useAddPlacement";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { useSetPlacement } from "@/hooks/sets/useSetPlacement";
import { useSetRequisition } from "@/hooks/sets/useSetRequisition";
import {
  defaultRequisitionQueryParams,
  defaultRequisitionStatusesForOffers,
  shiftTypeOptions,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { handleFunctions } from "@/lib/handleFunctions";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { mutations } from "@/lib/mutations";
import { nullableDateSchema, requiredDateSchema } from "@/lib/schemas";
import {
  CandidacyPlacement,
  CandidacyStatus,
  CompensationType,
  Job,
  PlacementStatus,
  Requisition,
  RequisitionStatus,
  RevenueType,
  ShiftType,
} from "@/types";

const placementSchema = z.object({
  backfillGuaranteeDays: z.coerce
    .number()
    .min(1, "Backfill guarantee days is required. Update the job form first."),
  baseCompensation: z.coerce.number().min(0, "Must be greater than 0"),
  // dateAccepted is required on a placement, a key difference from an offer where it is optional.
  // It signals that the candidate has accepted the offer, so if dateAccepted is unknown, it's a signal this is an offer not a placement.
  dateAccepted: requiredDateSchema,
  dateOffered: requiredDateSchema,
  dateSetToStart: nullableDateSchema,
  retentionBonusDays: z.coerce
    .number()
    .min(0, "Must be greater than 0")
    .nullable(),
  ownerUserId: z.coerce.number(),
  requisitionId: z.coerce.number(),
  retentionBonusAmount: z.coerce
    .number()
    .min(0, "Must be greater than 0")
    .nullable(),
  revenueEarned: z.coerce.number().min(0, "Must be greater than 0").nullable(),
  shiftDifferential: z.coerce
    .number()
    .min(0, "Must be greater than 0")
    .nullable(),
  shiftType: z.nativeEnum(ShiftType, { message: "Required" }),
  shiftTypeDescription: z.string().nullable(),
});

const schemaToPlacement = ({
  candidacyId,
  data,
  job,
}: {
  candidacyId: string;
  data: z.infer<typeof placementSchema>;
  job: Job;
}) => {
  return {
    ...data,
    candidacyId: candidacyId,
    compensationType: job.compensationType,
    dateAccepted: dates.convertStringDateToIso(data.dateAccepted),
    dateOffered:
      dates.convertStringDateToIso(data.dateOffered) ??
      new Date().toISOString(),
    dateSetToStart: dates.convertStringDateToIso(data.dateSetToStart),
    jobId: job.id,
    status: PlacementStatus.ACCEPTED,
  };
};

export const PlacementDialog = ({
  open,
  onOpenChange,
  placements,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placements: CandidacyPlacement[];
}) => {
  /* Contexts */
  const { candidacyId, job } = useSheetScope();
  const { userOptions } = useTaggableUsers();

  /* Database Queries and Cache Updates */
  // Reads
  const { data: candidacyInfo } = useGetCandidacyInfo({ candidacyId });
  const { data: requisitions, status } = useGetRequisitions({
    jobId: job.id,
    queryOptions: {
      ...defaultRequisitionQueryParams,
      statuses: defaultRequisitionStatusesForOffers,
    },
  });

  // Creates
  const { mutate: createPlacement } = useCreatePlacement();

  // Updates
  const { mutate: updatePlacement } = useUpdatePlacement();

  /* Cache Updates */
  // Adds
  const addPlacement = useAddPlacement();

  // Sets
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();
  const setPlacement = useSetPlacement();
  const setRequisition = useSetRequisition();

  // Misc Hooks
  const reportUnexpectedNull = useReportUnexpectedNull();

  /* React Hooks */
  const [selectedRequisition, setSelectedRequisition] = useState<
    Requisition | undefined
  >(undefined);

  const baseCompensation = misc.calculateEstimatedRevenueAmount(
    job.compensationType === CompensationType.HOURLY,
    job.compensationMin,
    job.compensationMax,
    job.percentOfSalary,
  );

  const form = useForm<z.infer<typeof placementSchema>>({
    resolver: zodResolver(
      // Revenue earned is required when the revenue type is percent and the selected requisition is not a backfill
      // Because backfills are for $0 and if revenue type is percent, revenue earned is calculated on job.percentOfSalary and job.compensationMin/Max.
      // Therefore, is revenue earned is null, it's a signal the job is not set up correctly.
      placementSchema.refine(
        (data) =>
          job.revenueType === RevenueType.PERCENT &&
          selectedRequisition?.status !== RequisitionStatus.BACKFILL
            ? !!data.revenueEarned
            : true,
        {
          message:
            "Revenue earned is required when the revenue type is percent. Check job form for missing information.",
          path: ["revenueEarned"],
        },
      ),
    ),
    defaultValues: {
      backfillGuaranteeDays: job.backfillGuaranteeDays ?? undefined,
      baseCompensation: baseCompensation === "" ? undefined : baseCompensation,
      dateAccepted: "",
      dateOffered: dates.formatIsoDateForUser(new Date().toISOString()) ?? "",
      dateSetToStart: "",
      retentionBonusDays: selectedRequisition?.retentionBonusDays || null,
      ownerUserId: undefined,
      requisitionId: undefined,
      retentionBonusAmount: selectedRequisition?.retentionBonusAmount || null,
      revenueEarned: undefined,
      shiftDifferential: null,
      shiftType: undefined,
      shiftTypeDescription: selectedRequisition?.shiftTypeDescription || null,
    },
  });

  // Prefill the form with the offer information if it exists (via the most recent "placement" which should be in the offered state)
  useEffect(() => {
    if (
      placements.length > 0 &&
      placements[0].status === PlacementStatus.OFFERED
    ) {
      const mostRecentPlacement = placements[0];
      setSelectedRequisition(mostRecentPlacement.requisition);
      form.reset({
        ...mostRecentPlacement,
        backfillGuaranteeDays: job.backfillGuaranteeDays ?? undefined,
        dateAccepted:
          dates.formatIsoDateForUser(mostRecentPlacement.dateAccepted) ?? "",
        dateOffered:
          dates.formatIsoDateForUser(mostRecentPlacement.dateOffered) ?? "",
        dateSetToStart:
          dates.formatIsoDateForUser(mostRecentPlacement.dateSetToStart) ?? "",
        retentionBonusDays: mostRecentPlacement.retentionBonusDays
          ? Number(mostRecentPlacement.retentionBonusDays)
          : null,
      });
    } else {
      // Covers unlikely scenario of user creating an offer, deleting it, and then creating a placement immediately after.
      form.reset();
    }
  }, [form, job, placements]);

  if (status === "pending") {
    return null;
  } else if (status === "error") {
    return null;
  }

  const handlePlaced = () => {
    setCandidacyInAllLists({
      candidacyId,
      lastCandidacyStatusChange: {
        createdAt: new Date().toISOString(),
        fromStatus: candidacyInfo?.status ?? null,
        toStatus: CandidacyStatus.PLACED,
        statusChangeReason: null,
        byAgent: false,
      },
    });
    setCandidacyInfo({
      candidacyId,
      status: CandidacyStatus.PLACED,
      lastStatusChangeByAgent: false,
    });
    form.reset();
    onOpenChange(false);
  };

  const handleSubmit = (data: z.infer<typeof placementSchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Mark placed",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      {
        has_date_set_to_start: !!data.dateSetToStart,
      },
    );
    if (!selectedRequisition) {
      reportUnexpectedNull(
        "PlacementDialog.handleSubmit",
        "selectedRequisition",
      );
      return;
    }
    const placementRequest = schemaToPlacement({
      candidacyId,
      data,
      job,
    });
    if (
      placements.length > 0 &&
      placements[0].status === PlacementStatus.OFFERED
    ) {
      mutations.updatePlacement({
        placementId: placements[0].id,
        setCandidacyInfo,
        setPlacement,
        setRequisition,
        onSuccess: handlePlaced,
        updatePlacement,
        updatePlacementRequest: {
          ...placementRequest,
          status: PlacementStatus.ACCEPTED,
        },
      });
    } else {
      mutations.createPlacement({
        addPlacement,
        createPlacement,
        placementRequest,
        setCandidacyInfo,
        onSuccess: handlePlaced,
      });
    }
  };

  // Only want to show live/backfill requisitions that don't currently have a placement in the offered or accepted state.
  // requisitions is already only Live/Backfill Reqs
  const requisitionsOpenToPlacement = requisitions.filter(
    (r) =>
      !r.placements.some((p) =>
        [PlacementStatus.OFFERED, PlacementStatus.ACCEPTED].includes(p.status),
      ),
  );

  const requisitionOptions =
    selectedRequisition &&
    !requisitionsOpenToPlacement.some((r) => r.id === selectedRequisition.id)
      ? [selectedRequisition, ...requisitionsOpenToPlacement]
      : requisitionsOpenToPlacement;

  return (
    <Dialog
      onOpenChange={(open) => {
        form.reset();
        onOpenChange(open);
        setSelectedRequisition(undefined);
      }}
      open={open}
    >
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fill out information about the placement</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <ChangeStatusDialogRequisitionSelect
              disabled={
                placements.length > 0 &&
                placements[0].status === PlacementStatus.OFFERED
              }
              fieldName="requisitionId"
              form={form}
              onRequisitionChange={(requisition: Requisition | undefined) => {
                handleFunctions.handleRequisitionChange({
                  form,
                  job,
                  requisition,
                  setSelectedRequisition,
                });
              }}
              // Only want to show live/backfill requisitions that don't currently have a placement in the offered or accepted state.
              requisitions={requisitionOptions}
            />
            <FormNumberSelect
              disabled={!selectedRequisition}
              fieldName="ownerUserId"
              form={form}
              label="Owner"
              options={userOptions}
            />
            <FormEnumSelect
              disabled={!selectedRequisition}
              fieldName="shiftType"
              form={form}
              label="Shift type"
              options={shiftTypeOptions.filter((option) =>
                selectedRequisition?.shiftType.includes(option.value),
              )}
            />
            <FormCurrencyInput
              disabled={!selectedRequisition}
              fieldName="baseCompensation"
              form={form}
              label={`Compensation ($${job.compensationType === CompensationType.HOURLY ? "/hr" : "/yr"})`}
              onChange={() => {
                handleFunctions.handleCompensationChange({
                  form,
                  job,
                  requisition: selectedRequisition,
                });
              }}
            />
            <FormCurrencyInput
              disabled={
                !selectedRequisition ||
                selectedRequisition.status === RequisitionStatus.BACKFILL ||
                job.revenueType === RevenueType.PERCENT
              }
              fieldName="revenueEarned"
              form={form}
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              label={`Revenue earned (${job.revenueType === RevenueType.PERCENT ? `${job.percentOfSalary ?? 0}% of annualized salary` : "fixed"})`}
              // Revenue Type can't be changed on a job that has placements, so this field should be filled for percent revenue type.
              optional={job.revenueType !== RevenueType.PERCENT}
              placeholder={
                selectedRequisition &&
                selectedRequisition.status === RequisitionStatus.BACKFILL
                  ? "$0"
                  : undefined
              }
            />
            <FormDateInput
              disabled={!selectedRequisition}
              fieldName="dateOffered"
              form={form}
              label="Date offered"
            />
            <FormDateInput
              disabled={!selectedRequisition}
              fieldName="dateAccepted"
              form={form}
              label="Date accepted"
            />
            <FormNumberInput
              disabled
              fieldName="backfillGuaranteeDays"
              form={form}
              label="Backfill guarantee days (must be edited from job form)"
            />
            <FormDateInput
              disabled={!selectedRequisition}
              fieldName="dateSetToStart"
              form={form}
              label="Date set to start"
              optional
            />
            <FormTextInput
              fieldName="shiftTypeDescription"
              form={form}
              label="Shift type description (details about shift type)"
              optional
            />
            <FormCurrencyInput
              disabled={
                !selectedRequisition ||
                job.compensationType !== CompensationType.HOURLY
              }
              fieldName="shiftDifferential"
              form={form}
              label="Shift differential"
              optional
              placeholder={
                job.compensationType === CompensationType.HOURLY
                  ? "$0 /hr"
                  : "N/A"
              }
            />
            <FormNumberInput
              disabled={!selectedRequisition}
              fieldName="retentionBonusDays"
              form={form}
              label="Retention bonus days"
              optional
            />
            <FormCurrencyInput
              disabled={!selectedRequisition}
              fieldName="retentionBonusAmount"
              form={form}
              label="Retention bonus amount ($$)"
              optional
            />
            <Row className="justify-end">
              <Button className="rounded-full" type="submit">
                Submit
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
